import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon } from '@/components/kit';
import { useShellChrome } from '@/layouts/shellChrome';
import { usePageMeta } from '@/hooks/usePageMeta';
import { money } from '../lib/scope';
import { useWallet, walletRunwayWeeks } from '../lib/useManagerData';
import { ManagerSurface } from '../components/ManagerSurface';
import { ManagerMenu } from '../components/ManagerMenu';
import { SETTINGS_DOORS, type SettingsDoor } from './doors';
import { useProviderCount, useSubscriptionLine } from './useSettings';
import { useFlowTemplates } from '@/pages/doctor/shared/useFlows';

/**
 * Clinic manager · System Settings home — `g_set.dc.html`.
 *
 * THIRTEEN DOORS, FULL WIDTH, NO PANE — a grid of three columns, and the
 * board's closing line explains the shape of everything behind it: each door
 * opens onto the same thirteen folded into a single left column, so Settings is
 * never more than one click wide.
 *
 * FOUR OF THE SUB-LINES ARE FIGURES, not phrases: the plan and its next
 * invoice, the wallet's balance and runway, the size of the Directory, the
 * number of flows. Each is read from the endpoint the screen behind that door
 * would read. Where the role is refused the figure (money is the owner's) or
 * the capability does not exist yet, the door says which — never a zero, which
 * a manager would read as "we have none".
 */
export function ManagerSettingsPage() {
  const go = useNavigate();
  const [query, setQuery] = useState('');

  const { wallet } = useWallet();
  const providerCount = useProviderCount();
  const subscription = useSubscriptionLine();
  const flows = useFlowTemplates();

  usePageMeta({ title: 'System Settings' });

  const runway = walletRunwayWeeks(wallet);
  const walletLine = wallet
    ? `${money(wallet.balanceMinor / 100, wallet.currency || 'EGP')}${runway === null ? ' · nothing spent yet' : ` · about ${runway} week${runway === 1 ? '' : 's'}`}`
    : 'balance not readable';

  const subscriptionLine = subscription.refused
    ? 'the plan is the owner’s to see'
    : subscription.plan
      ? `${subscription.plan.tier ?? 'plan not named'}${subscription.plan.currentPeriodEnd
        ? ` · next invoice ${new Date(subscription.plan.currentPeriodEnd).getDate()} ${new Date(subscription.plan.currentPeriodEnd).toLocaleString('en-GB', { month: 'short' })}`
        : ''}`
      : 'plan not readable';

  const subOf = (d: SettingsDoor): string => {
    if (d.live === 'wallet') return walletLine;
    if (d.live === 'subscription') return subscriptionLine;
    if (d.live === 'directory') {
      return providerCount === null
        ? 'external providers' : `${providerCount} external provider${providerCount === 1 ? '' : 's'}`;
    }
    /* "3 flows · the shared editor" — the count is the templates endpoint
       Settings ▸ Flows itself reads, and it is left out (not zeroed) until
       that answer arrives or when the read is refused. */
    if (d.live === 'flows') {
      return flows.isSuccess
        ? `${flows.data.length} flow${flows.data.length === 1 ? '' : 's'} · the shared editor`
        : 'the shared editor';
    }
    return d.sub;
  };

  const doors = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return SETTINGS_DOORS;
    return SETTINGS_DOORS.filter((d) => `${d.title} ${subOf(d)}`.toLowerCase().includes(q));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, walletLine, subscriptionLine, providerCount]);

  useShellChrome(
    {
      raw: true,
      phoneMenu: (close) => <ManagerMenu door="settings" onClose={close} />,
      title: 'System Settings',
      sub: 'facility, branding and preferences · the clinic manager’s own door',
      search: 'Search a setting — a field, a switch, a person…',
      searchValue: query,
      onSearchChange: setQuery,
      /* NO PRIMARY. The board's top bar carries the search and the bell and
         nothing else — an index of doors has no one action (§13.3). */
      // NO PANE — a door is not a selection (Law 4).
    },
    [query, walletLine, subscriptionLine, providerCount],
  );

  return (
    <div className="maie desk">
      {/* 361px: this board's own search field. The artboard flex-sizes it and
          its long title column leaves it narrower than the canvas's 380px cap
          — see manager.css §2. */}
      <ManagerSurface searchWidth={361} />
      <div className="dcontent">
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 9, marginTop: 4 }}>
          {doors.map((d) => {
            const inner = (
              <>
                {/* `ic-lg` — the 20px box the artboard draws every door with.
                    The default 16px box is 4px narrower and walks the whole
                    title column left of the board (round-2 MAJOR). */}
                <span className="brand"><Icon name={d.icon} size="lg" /></span>
                <div style={{ minWidth: 0 }}>
                  <p className="t-strong">{d.title}</p>
                  <p className="t-cap mut">{subOf(d)}</p>
                </div>
              </>
            );
            const style = {
              width: 'calc(33.333% - 6px)', boxSizing: 'border-box' as const, margin: 0,
              display: 'flex', gap: 10, alignItems: 'center',
            };
            return (
              <button key={d.key} type="button" className="card text-start cm-door"
                style={{ ...style, cursor: 'pointer' }} onClick={() => go(d.to, d.key === 'directory' ? { state: { settingsFrame: true } } : undefined)}>
                {inner}
              </button>
            );
          })}
        </div>

        {doors.length === 0 ? (
          <p className="t-sec mut" style={{ marginTop: 12 }}>
            No setting matches that. Clear the search to see all thirteen doors again.
          </p>
        ) : (
          <p className="t-sec mut" style={{ marginTop: 12 }}>
            Thirteen doors. Behind every one of them this grid stands folded into a single column
            on the left, so nothing in Settings is ever more than one click from anything else
            in it.
          </p>
        )}
      </div>
    </div>
  );
}
